import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useIsGuestUser, useStoreId } from '@/features/account/services/User/hooks'
import { useHydrated } from '@/features/shared/components/ClientOnly'
import { useQueryPlaRegionById } from '@/features/shop/services/PLA/queries'
import {
  pdpProductQueryOptions,
  useQueryPdpProduct,
} from '@/features/shop/services/Product/queries'
import { PLA_POPOVER_SESSION_STORAGE } from '@/features/shop/components/ProductDetail/constants'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { type GuestAddressOnSubmitParams } from '@/features/account/hooks/address/useOnSubmitGuestAddress'
import { getSessionStorageItem } from '@/features/shared/utils/sessionStorage'
import { type PLAGuestWithZipOnlyHeaderOnSubmit } from '@/features/shared/components/Headers/PLAGuestWithZipOnlyHeader'
import { DynamicGuestAddressModal } from '@/features/account/components/Modal/GuestAddressModal/DynamicGuestAddressModal'
import { usePathname } from 'next/navigation'

export const usePlaEligibility = (productId: number) => {
  const storeId = useStoreId()
  const plaRegion = useQueryPlaRegionById().data
  const pdpShelves = useQueryPdpProduct(productId).data

  return Boolean(storeId && storeId === plaRegion?.retailer_id && pdpShelves)
}

export const usePlaPopoverEligibility = (productId: number) => {
  const isPlaEligible = usePlaEligibility(productId)
  const isHydrated = useHydrated()
  const { street1 } = useGlobalDefaultShoppingAddress()

  return (
    street1 && isPlaEligible && isHydrated && !getSessionStorageItem(PLA_POPOVER_SESSION_STORAGE)
  )
}

export const useIsGuestWithZipOnly = () => {
  const isGuestUser = useIsGuestUser()
  const { street1, zip_code } = useGlobalDefaultShoppingAddress()

  return Boolean(isGuestUser && zip_code && !street1)
}

export const useOpenGuestAddressModalForPLA = () => {
  const pathname = usePathname() ?? ''
  const { openDialog } = useHandleDialog()

  return useCallback(
    (
      args: void | React.MouseEvent | { onSubmit?: (params: GuestAddressOnSubmitParams) => void },
    ) => {
      openDialog(DynamicGuestAddressModal, {
        onSubmit: args && 'onSubmit' in args ? args.onSubmit : undefined,
        showLoginLink: true,
        isCancellable: false,
        startChangeStep: true,
        contentLabel: 'Enter your delivery address',
        subheading: 'Item availability and price may vary by location.',
        nextUrl: pathname,
      })
    },
    [openDialog, pathname],
  )
}

export const useSubmitZipForPlaPopover = (productId: number) => {
  const queryClient = useQueryClient()

  return async ({ store, storeParams }: Parameters<PLAGuestWithZipOnlyHeaderOnSubmit>[0]) => {
    try {
      const result = await queryClient.query(
        pdpProductQueryOptions({
          storeParams,
          productId,
        }),
      )
      if (!result.product) {
        throw new Error('Product unavailable')
      }
    } catch (e) {
      throw new Error(`Product unavailable at ${store.name} within ZIP code`)
    }
  }
}
